import React, { useState } from 'react';
import TagSelector from './TagSelector';

/**
 * ListingsPage Component
 * Extracted from App.js to improve code organization and loading performance.
 * This is the main Listings grid view showing all property appointments.
 */
const ListingsPage = ({
  // Data
  appointments,
  clients,
  tasks,
  teamMembers,
  appointmentThumbnails,
  products,
  availableTags,
  // State setters for navigation
  setActiveTab,
  setSelectedListingForEdit,
  setPropertyClients,
  setShowAppointmentForm,
  setEditingTask,
  setTaskForm,
  setShowTaskForm,
  setAppointments,
  // Functions
  fetchAppointmentMedia,
  formatAppointmentDate,
  showToast,
  API,
  axios
}) => {
  // Local state for filters (moved from App.js)
  const [listingFilters, setListingFilters] = useState({
    dateFrom: '',
    dateTo: '',
    clientName: '',
    status: '',
    photographerId: '',
    search: ''
  });
  const [showListingFilters, setShowListingFilters] = useState(false);
  const [loadingListingId, setLoadingListingId] = useState(null);

  // Safe arrays
  const safeAppointments = Array.isArray(appointments) ? appointments : [];
  const safeTasks = Array.isArray(tasks) ? tasks : [];
  const safeClients = Array.isArray(clients) ? clients : [];
  const safeTeamMembers = Array.isArray(teamMembers) ? teamMembers : [];
  const safeProducts = Array.isArray(products) ? products : [];

  // Calculate order total from selected products
  const calculateOrderTotal = (appointment) => {
    if (!appointment.selected_products || appointment.selected_products.length === 0) {
      return null;
    }
    
    const sqft = appointment.property_sqft || 0;
    let total = 0;
    
    appointment.selected_products.forEach(productId => {
      const product = safeProducts.find(p => p.id === productId);
      if (product) {
        // Check for pricing tiers
        if (product.pricing_tiers && product.pricing_tiers.length > 0 && sqft > 0) {
          const tier = product.pricing_tiers.find(t => sqft >= t.min_sqft && sqft <= t.max_sqft);
          if (tier) {
            total += tier.price || 0;
          } else {
            total += product.price || 0;
          }
        } else {
          total += product.price || 0;
        }
      }
    });
    
    return total > 0 ? total.toFixed(2) : null;
  };

  // Filter appointments based on filters
  const filteredAppointments = safeAppointments.filter(appointment => {
    if (listingFilters.search && !appointment.property_address?.toLowerCase().includes(listingFilters.search.toLowerCase())) return false;
    if (listingFilters.dateFrom && appointment.scheduled_date < listingFilters.dateFrom) return false;
    if (listingFilters.dateTo && appointment.scheduled_date > listingFilters.dateTo) return false;
    if (listingFilters.clientName) {
      const clientName = safeClients.find(c => c.id === appointment.client_id)?.name?.toLowerCase() || '';
      const coClientNames = (appointment.client_ids || []).map(id => safeClients.find(c => c.id === id)?.name?.toLowerCase() || '');
      const allClientNames = [clientName, ...coClientNames].join(' ');
      if (!allClientNames.includes(listingFilters.clientName.toLowerCase())) return false;
    }
    if (listingFilters.photographerId && appointment.photographer_id !== listingFilters.photographerId) return false;
    if (listingFilters.status && appointment.status !== listingFilters.status) return false;
    return true;
  });

  const hasActiveFilters = listingFilters.dateFrom || listingFilters.dateTo || listingFilters.clientName || listingFilters.status || listingFilters.photographerId || listingFilters.search;

  const handleEditListing = async (appointment) => {
    setLoadingListingId(appointment.id);
    try {
      setSelectedListingForEdit(appointment);
      setPropertyClients(appointment.client_ids || []);
      // Navigate immediately, fetch media in background
      setActiveTab('edit-listing', { listingId: appointment.id });
      // Fetch media after navigation (non-blocking)
      fetchAppointmentMedia(appointment.id);
    } finally {
      // Small delay before clearing loading to ensure smooth transition
      setTimeout(() => setLoadingListingId(null), 300);
    }
  };

  const handleAddTask = (appointment) => {
    setEditingTask(null);
    setTaskForm({
      appointment_id: appointment.id,
      propertySearch: `${appointment.property_address} - ${appointment.client_name}`,
      title: '',
      description: '',
      due_date: '',
      assigned_to: '',
      priority: 'medium'
    });
    setShowTaskForm(true);
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center gap-4">
        <h2 className="text-2xl font-bold">Listings</h2>
        <button
          onClick={() => setShowAppointmentForm(true)}
          className="clara-btn-primary px-6 py-3 ml-4"
          data-testid="add-property-btn"
        >
          + Add Property
        </button>
      </div>

      {/* Collapsible Filter Section */}
      <div className="rounded-xl shadow-sm border border-gray-200 overflow-hidden" style={{ backgroundColor: '#EFF6FF' }}>
        <button 
          onClick={() => setShowListingFilters(!showListingFilters)} 
          className="w-full flex items-center justify-between p-4 hover:bg-blue-100 transition-colors"
          data-testid="filter-listings-toggle"
        >
          <span className="font-medium text-gray-700">
            🔍 Filter Listings 
            {hasActiveFilters && (
              <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">Active</span>
            )}
          </span>
          <svg 
            className={`w-5 h-5 text-gray-500 transform transition-transform ${showListingFilters ? 'rotate-180' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {showListingFilters && (
          <div className="p-4 border-t border-blue-200">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                <input 
                  type="text" 
                  placeholder="Address..." 
                  value={listingFilters.search} 
                  onChange={(e) => setListingFilters({...listingFilters, search: e.target.value})} 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="filter-search"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                <input 
                  type="date" 
                  value={listingFilters.dateFrom} 
                  onChange={(e) => setListingFilters({...listingFilters, dateFrom: e.target.value})} 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="filter-date-from"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                <input 
                  type="date" 
                  value={listingFilters.dateTo} 
                  onChange={(e) => setListingFilters({...listingFilters, dateTo: e.target.value})} 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="filter-date-to"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
                <input 
                  type="text" 
                  placeholder="Client name..." 
                  value={listingFilters.clientName} 
                  onChange={(e) => setListingFilters({...listingFilters, clientName: e.target.value})} 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="filter-client"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Photographer</label>
                <select 
                  value={listingFilters.photographerId} 
                  onChange={(e) => setListingFilters({...listingFilters, photographerId: e.target.value})} 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="filter-photographer"
                >
                  <option value="">All Photographers</option>
                  {safeTeamMembers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select 
                  value={listingFilters.status} 
                  onChange={(e) => setListingFilters({...listingFilters, status: e.target.value})} 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="filter-status"
                >
                  <option value="">All Statuses</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button 
                onClick={() => setListingFilters({ dateFrom: '', dateTo: '', clientName: '', status: '', photographerId: '', search: '' })} 
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                data-testid="clear-filters-btn"
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Property Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="listings-grid">
        {filteredAppointments.map((appointment, index) => {
          const thumbnail = appointmentThumbnails[appointment.id];
          const orderNumber = `#${(5500 + index + 1)}`;
          const activeTasks = safeTasks.filter(t => t.appointment_id === appointment.id && t.status !== 'completed');
          const orderTotal = calculateOrderTotal(appointment);
          const isLoading = loadingListingId === appointment.id;
          
          return (
            <div 
              key={appointment.id} 
              className={`bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden border border-gray-100 ${isLoading ? 'opacity-75' : ''}`}
              data-testid={`listing-card-${appointment.id}`}
            >
              {/* Main Photo */}
              <div 
                className="aspect-[5/3] bg-gray-100 flex items-center justify-center relative cursor-pointer"
                onClick={() => handleEditListing(appointment)}
              >
                {thumbnail ? (
                  <img 
                    src={thumbnail}
                    alt={`${appointment.property_address} thumbnail`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <svg className="w-16 h-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5a1.5 1.5 0 001.5-1.5V4.5a1.5 1.5 0 00-1.5-1.5H3.75a1.5 1.5 0 00-1.5 1.5v15a1.5 1.5 0 001.5 1.5z" />
                  </svg>
                )}
                
                {/* Loading Overlay */}
                {isLoading && (
                  <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center">
                    <div className="flex flex-col items-center gap-2">
                      <svg className="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-sm text-gray-600 font-medium">Loading...</span>
                    </div>
                  </div>
                )}
                
                {/* Status Badges */}
                <div className="absolute top-3 left-3 flex gap-2">
                  {appointment.status === 'completed' && (
                    <>
                      <span className="bg-green-600 text-white px-3 py-1 rounded-md text-xs font-semibold">
                        Delivered
                      </span>
                      <span className="bg-green-600 text-white px-3 py-1 rounded-md text-xs font-semibold">
                        Paid
                      </span>
                    </>
                  )}
                  {appointment.status === 'scheduled' && (
                    <span className="bg-blue-600 text-white px-3 py-1 rounded-md text-xs font-semibold">
                      Scheduled
                    </span>
                  )}
                  {appointment.status === 'in_progress' && (
                    <span className="bg-yellow-600 text-white px-3 py-1 rounded-md text-xs font-semibold">
                      In Progress
                    </span>
                  )}
                </div>
                
                {/* Edit Icon */}
                <div className="absolute bottom-3 right-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditListing(appointment);
                    }}
                    className="bg-white bg-opacity-80 hover:bg-opacity-100 p-2 rounded-md transition-all"
                    data-testid={`edit-listing-${appointment.id}`}
                    disabled={isLoading}
                  >
                    <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Property Details */}
              <div className="p-4">
                {/* Address */}
                <h3 className="text-base font-bold text-gray-900 mb-1 leading-tight" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                  {appointment.property_address}
                </h3>
                
                {/* Client Info */}
                <div className="mb-3">
                  <p className="text-gray-900 font-semibold text-sm mb-1">
                    {appointment.client_name}
                  </p>
                  <p className="text-gray-600 text-xs">
                    {safeClients.find(c => c.id === appointment.client_id)?.email || 'client@example.com'}
                  </p>
                </div>

                {/* Tags */}
                {(availableTags || []).length > 0 && (
                  <div className="mb-3" data-testid={`listing-tags-${appointment.id}`} onClick={(e) => e.stopPropagation()}>
                    <TagSelector
                      selectedTagIds={appointment.tag_ids || []}
                      availableTags={availableTags || []}
                      onChange={async (newTagIds) => {
                        try {
                          await axios.put(`${API}/appointments/${appointment.id}/tags`, { tag_ids: newTagIds });
                          setAppointments(prev => prev.map(a => a.id === appointment.id ? { ...a, tag_ids: newTagIds } : a));
                        } catch (err) {
                          showToast?.('Failed to update tags', 'error');
                        }
                      }}
                      compact={true}
                    />
                  </div>
                )}

                {/* Order Info */}
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-700 font-medium text-xs">
                    Order {orderNumber}
                    {appointment.coupon_code && (
                      <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded bg-green-100 text-green-700 text-[10px] font-semibold">
                        {appointment.coupon_code}
                      </span>
                    )}
                  </span>
                  <span className="text-gray-900 font-semibold text-xs">
                    {orderTotal ? `$${orderTotal}` : '—'} ({appointment.selected_products?.length || 0} {appointment.selected_products?.length === 1 ? 'service' : 'services'})
                  </span>
                </div>
                
                {/* Date/Time in Light Grey Background */}
                <div className="bg-gray-100 rounded-lg px-3 py-2 mb-3">
                  <div className="text-gray-700 text-xs font-medium">
                    {formatAppointmentDate(appointment.scheduled_date, appointment.scheduled_time)}
                  </div>
                </div>

                {/* Tasks Section */}
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold tracking-wide">
                      <span className="text-gray-700 dark:text-gray-300 uppercase">Tasks: </span>
                      {activeTasks.length > 0 && (
                        <span className="text-red-600 dark:text-red-400">
                          {activeTasks.length} Active
                        </span>
                      )}
                    </p>
                    <button
                      onClick={() => handleAddTask(appointment)}
                      className="clara-btn-secondary py-1 px-3 text-xs flex items-center gap-1"
                      title="Add Task"
                      data-testid={`add-task-${appointment.id}`}
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add Task
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {safeAppointments.length === 0 && (
        <div className="text-center py-12" data-testid="listings-empty-state">
          <div className="text-6xl mb-4">🏠</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Properties Yet</h3>
          <p className="text-gray-600 mb-4">Get started by adding your first property listing</p>
          <button
            onClick={() => setShowAppointmentForm(true)}
            className="clara-btn-primary px-8 py-4"
            data-testid="add-first-property-btn"
          >
            + Add Property
          </button>
        </div>
      )}
    </div>
  );
};

export default ListingsPage;
